import { create } from 'zustand';
import type { Transform } from './types';

export interface Product {
  id: string;
  name: string;
  defaultPrefix: string;
  width: number;
  height: number;
  maskType: string;
  enabled: boolean;
  tab: 'mockups' | 'products';
  category?: string;
  image?: string;
  mask?: string;
  overlay?: string;
  mockupWidth?: number;
  mockupHeight?: number;
  mockupMask?: string;
  mockupOverlay?: string;
  dpi?: number;
  renderRotation?: number;
  blendMode?: string;
  linkedMockups?: string[];
}

export interface FileItem {
  name: string;
  url: string;
  thumb: string;
  mtime: number;
  size: number;
  type: 'upload' | 'cloud' | 'linked' | 'publication';
  article?: string;
  category?: string;
  short_url?: string;
  print_name?: string | null;
  source_name?: string | null;
  source_url?: string | null;
  product_name?: string;
  render_prefix?: string;
  is_remote?: boolean;
  is_placeholder?: boolean;
  article_thumb?: string | null;
  article_created_mtime?: number;
  category_id?: string;
  category_name?: string;
  category_source?: 'auto' | 'manual';
  meta?: Record<string, unknown>;
  id?: string;
  prompt?: string;
  model?: string;
}

export interface PrintItem {
  id: string;
  name: string;
  url: string;
  thumb: string;
  article: string;
  printName: string;
  positions: Record<string, Transform | { mockups: Transform; products: Transform }>;
  type?: string;
}

export interface CloudProgress {
  total: number;
  done: number;
  current: string;
}

interface AppState {
  // Auth
  password: string;

  // Data
  files: FileItem[];
  products: Product[];
  printsConfig: Record<string, unknown>;

  // UI
  activeTab: string;
  galleryTab: string;
  cloudMode: string;
  activeProductId: string | null;
  selectedPrint: FileItem | PrintItem | null;
  mockupsPerRow: number;
  saveStatus: 'saved' | 'saving' | 'error';
  maskColor: string | null;

  // Modals
  showSettingsModal: boolean;

  // Loading states
  isExporting: boolean;
  isUploading: boolean;
  isCloudSaving: boolean;
  cloudProgress: CloudProgress;

  // Transform maps
  transforms: Record<string, Transform>;
  productTransforms: Record<string, Transform>;

  // Setters
  setPassword: (p: string) => void;
  setFiles: (files: FileItem[]) => void;
  setProducts: (products: Product[]) => void;
  setPrintsConfig: (config: Record<string, unknown>) => void;
  setActiveTab: (tab: string) => void;
  setGalleryTab: (tab: string) => void;
  setCloudMode: (mode: string) => void;
  setActiveProductId: (id: string | null) => void;
  setSelectedPrint: (print: FileItem | PrintItem | null) => void;
  setMockupsPerRow: (n: number) => void;
  setSaveStatus: (s: 'saved' | 'saving' | 'error') => void;
  setMaskColor: (c: string | null) => void;
  setShowSettingsModal: (v: boolean) => void;
  setIsExporting: (v: boolean) => void;
  setIsUploading: (v: boolean) => void;
  setIsCloudSaving: (v: boolean) => void;
  setCloudProgress: (p: CloudProgress | ((prev: CloudProgress) => CloudProgress)) => void;
  setTransforms: (
    t: Record<string, Transform> | ((prev: Record<string, Transform>) => Record<string, Transform>),
  ) => void;
  setProductTransforms: (
    t: Record<string, Transform> | ((prev: Record<string, Transform>) => Record<string, Transform>),
  ) => void;
}

export const useAppStore = create<AppState>((set) => ({
  password: '',
  files: [],
  products: [],
  printsConfig: {},
  activeTab: 'products',
  galleryTab: 'files',
  cloudMode: 'mockups',
  activeProductId: null,
  selectedPrint: null,
  mockupsPerRow: 2,
  saveStatus: 'saved',
  maskColor: null,
  showSettingsModal: false,
  isExporting: false,
  isUploading: false,
  isCloudSaving: false,
  cloudProgress: { total: 0, done: 0, current: '' },
  transforms: {},
  productTransforms: {},

  setPassword: (password) => set({ password }),
  setFiles: (files) => set({ files }),
  setProducts: (products) => set({ products }),
  setPrintsConfig: (printsConfig) => set({ printsConfig }),
  setActiveTab: (activeTab) => set({ activeTab }),
  setGalleryTab: (galleryTab) => set({ galleryTab }),
  setCloudMode: (cloudMode) => set({ cloudMode }),
  setActiveProductId: (activeProductId) => set({ activeProductId }),
  setSelectedPrint: (selectedPrint) => set({ selectedPrint }),
  setMockupsPerRow: (mockupsPerRow) => set({ mockupsPerRow }),
  setSaveStatus: (saveStatus) => set({ saveStatus }),
  setMaskColor: (maskColor) => set({ maskColor }),
  setShowSettingsModal: (showSettingsModal) => set({ showSettingsModal }),
  setIsExporting: (isExporting) => set({ isExporting }),
  setIsUploading: (isUploading) => set({ isUploading }),
  setIsCloudSaving: (isCloudSaving) => set({ isCloudSaving }),
  setCloudProgress: (p) =>
    set((state) => ({
      cloudProgress: typeof p === 'function' ? p(state.cloudProgress) : p,
    })),
  setTransforms: (t) =>
    set((state) => ({
      transforms: typeof t === 'function' ? t(state.transforms) : t,
    })),
  setProductTransforms: (t) =>
    set((state) => ({
      productTransforms: typeof t === 'function' ? t(state.productTransforms) : t,
    })),
}));
